import { OVERLAY_CLOSE_ELEMENT_ATTR } from './constants';
import type {
    HandleCloseParams,
    IOverlayItemModel,
    IParentModel,
    OverlayCloseSource,
    OverlayOptions,
} from './types';

let nextOrder = 0;

// @analyze-exports:disable
export class OverlayItemModel implements IParentModel, IOverlayItemModel {
    readonly order = ++nextOrder;

    private options: OverlayOptions = {
        closeStrategy: 'pressdown',
    };

    private children = new Set<number>();
    private childClosed = false;

    constructor(
        private parent?: IParentModel,
        public name?: string,
    ) {}

    updateParams(options: OverlayOptions): void {
        Object.assign(this.options, options);
    }

    updateVisible(visible: boolean): void {
        if (!visible) {
            this.parent?.removeChild(this);
        } else {
            this.parent?.addChild(this);
        }
    }

    canClose({ source, strategy, target }: HandleCloseParams): boolean {
        const { onClose, closeStrategy, checkLastCloses } = this.options;

        if (!onClose || (strategy && strategy !== closeStrategy)) {
            return false;
        }

        if (source === 'click' && this.isEssentionalClick(target as HTMLElement | null)) {
            return false;
        }

        const canClose = !this.children.size && (!checkLastCloses || !this.childClosed);

        if (!canClose) {
            return false;
        }

        return true;
    }

    close({ event, source }: HandleCloseParams): void {
        if (this.options.onClose && event) {
            this.options.onClose?.(event, source);
        }

        this.parent?.removeChild(this, source);
    }

    cleanAfterCloses(): void {
        this.childClosed = false;
    }

    removeChild(child: IOverlayItemModel, source?: OverlayCloseSource): void {
        if (!this.children.has(child.order)) {
            return;
        }
        this.children.delete(child.order);
        if (source === 'click') {
            this.childClosed = true;
        }
    }

    addChild(child: IOverlayItemModel): void {
        this.children.add(child.order);
    }

    containsTarget(target: EventTarget | null): boolean {
        if (!target || !(target instanceof HTMLElement)) {
            return false;
        }

        return (this.options.essentialRefs || []).some((ref) => {
            return ref.current instanceof HTMLElement && ref.current.contains(target);
        });
    }

    private isEssentionalClick(target: HTMLElement | null) {
        if (target?.closest && target.closest(`[${OVERLAY_CLOSE_ELEMENT_ATTR}="true"]`)) {
            return false;
        }
        return (this.options.essentialRefs || []).some((ref) => {
            return ref.current instanceof HTMLElement && ref.current.contains(target);
        });
    }
}
